import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Container,
  Paper,
  Grid,
  InputAdornment,
  Fade,
  LinearProgress,
  CircularProgress,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Autocomplete,
  FormControl,
  InputLabel,
  alpha
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  MyLocation as MyLocationIcon,
  Save as SaveIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  Update as UpdateIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  Visibility as VisibilityIcon,
  QrCode as QrCodeIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LoaderWrapper } from './index';
import { LocationAPI } from '../services/locationApi';
import { decryptAppId, decryptText, encryptAppId, encryptText } from '../utils/encryption';
import DoApiService from '../services/DoApiService';
import s3Service from '../services/s3Service';
import LaboratoryService from '../services/laboratoryService';
import CalibrationLaboratoryService from '../services/calibrationlaboratoryService';
import jsPDF from 'jspdf';
import * as QRCodeLib from 'qrcode';
import axios from 'axios';

// Modern color palette
const colors = {
  primary: {
    main: "#6366f1",
    light: "#818cf8",
    dark: "#4f46e5",
    contrast: "#ffffff",
  },
  secondary: {
    main: "#ec4899",
    light: "#f472b6",
    dark: "#db2777",
  },
  success: {
    main: "#10b981",
    light: "#34d399",
    dark: "#059669",
  },
  warning: {
    main: "#f59e0b",
    light: "#fbbf24",
    dark: "#d97706",
  },
  error: {
    main: "#ef4444",
    light: "#f87171",
    dark: "#dc2626",
  },
  grey: {
    50: "#f8fafc",
    100: "#f1f5f9",
    200: "#e2e8f0",
    300: "#cbd5e1",
    400: "#94a3b8",
    500: "#64748b",
    600: "#475569",
    700: "#334155",
    800: "#1e293b",
    900: "#0f172a",
  },
};

// Constants
const S3_BASE_URL = 'https://nabl7t.s3.ap-south-1.amazonaws.com/live/';

// Validation schema
const schema = yup.object({
  latitude: yup
    .number()
    .required('Latitude is required')
    .min(-90, 'Latitude must be between -90 and 90 degrees')
    .max(90, 'Latitude must be between -90 and 90 degrees'),
  longitude: yup
    .number()
    .required('Longitude is required')
    .min(-180, 'Longitude must be between -180 and 180 degrees')
    .max(180, 'Longitude must be between -180 and 180 degrees'),
});

const LaboratoryUpdateDetailsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [labTypeId, setLabTypeId] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [coordinatesValid, setCoordinatesValid] = useState(null);
  const [error, setError] = useState('');
  const [gettingLocation, setGettingLocation] = useState(false);
  const [labInfo, setLabInfo] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [assessorsData, setAssessorsData] = useState([]);

  // File upload popup states
  const [uploadPopupOpen, setUploadPopupOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [currentAssessor, setCurrentAssessor] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [s3UploadedFiles, setS3UploadedFiles] = useState([]); // Track S3 uploaded files
  const [autoUploading, setAutoUploading] = useState(false); // Track auto-upload progress
  const [selectedClauseNo, setSelectedClauseNo] = useState(null); // Track selected clause number
  const [assessorFileStats, setAssessorFileStats] = useState({}); // Track file counts by assessor
  const [existingFiles, setExistingFiles] = useState([]); // Track existing files for current assessor
  const [loadingExistingFiles, setLoadingExistingFiles] = useState(false); // Loading state for existing files
  const [clauseNoList, setClauseNoList] = useState([]); // Clause number list for dropdown
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false); // Delete confirmation dialog
  const [fileToDelete, setFileToDelete] = useState(null); // File to be deleted
  const [deletingFile, setDeletingFile] = useState(false); // Delete loading state

  // Lab details and assessment stop state
  const [labDetails, setLabDetails] = useState(null);
  const [loadingLabDetails, setLoadingLabDetails] = useState(false);
  const [assessmentStopped, setAssessmentStopped] = useState(false);

  // Equipment data and QR code functionality states
  const [equipmentData, setEquipmentData] = useState([]);

  // Password verification states
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordVerifying, setPasswordVerifying] = useState(false);
  const [pendingApprovalFacility, setPendingApprovalFacility] = useState(null);
  const [loadingEquipments, setLoadingEquipments] = useState(false);
  const [qrCodeDownloading, setQrCodeDownloading] = useState(false);

  // Application stage state
  const [applicationStage, setApplicationStage] = useState('1');

  // Facility location states
  const [formData, setFormData] = useState({ appId: null });
  const [facilities, setFacilities] = useState([]);
  const [facilitiesLoading, setFacilitiesLoading] = useState(false);
  const [facilityLocations, setFacilityLocations] = useState({});
  const [gettingFacilityLocation, setGettingFacilityLocation] = useState(null);
  
  // Ref to track last validated coordinates to prevent duplicate API calls
  const lastValidatedRef = useRef({ lat: null, lng: null });



  // Extract parameters from URL
  const encryptedOrgId = searchParams.get('orgId');
  const encryptedAppId = searchParams.get('appId');

  // Decrypt parameters
  let orgId, appId;
  try {
    orgId = encryptedOrgId ? decryptText(decodeURIComponent(encryptedOrgId)) : null;
    appId = encryptedAppId ? decryptAppId(decodeURIComponent(encryptedAppId)) : null;
  } catch (error) {
    console.error('Failed to decrypt parameters:', error);
    // Fallback to direct parameters if decryption fails
    orgId = searchParams.get('orgId');
    appId = searchParams.get('appId');
  }

  const { control, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      latitude: '',
      longitude: '',
    }
  });

  // Get application stage from API
  const getApplicationStage = async (appId) => {
    try {
      if (!appId) return;

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/laboratory/application-stage/${appId}`);
      const result = await response.json();

      if (result.success) {
        setApplicationStage(result.stage);
        setLabTypeId(result.labTypeId);
      } else {
        setApplicationStage('1'); // Default to 1
      }
    } catch (error) {
      console.error('Error fetching application stage:', error);
      setApplicationStage('1'); // Default to 1
    }
  };

  // Load assessor allotment data using mode 5
  const loadAssessorAllotmentData = useCallback(async () => {
    if (!appId) {
      setError("Invalid application ID. Please check the URL.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Call API to get assessor allotment details (mode 5 - AssessorAllotment_New)
      const response = await DoApiService.getAssessorAllotmentDetails9({
        mode: 64,
        appId: parseFloat(appId),
      });

      if (response.success) {
        setAssessorsData(response.data || []);
        // Log capacity information for debugging
        response.data?.forEach((assessor, index) => {
          console.log(`Assessor ${index + 1}:`, {
            name: assessor.assessorName || assessor.name,
            id: assessor.allotedToId,
            capacity: assessor.capacity,
            fullData: assessor
          });
        });
      } else {
        throw new Error(response.message || "Failed to load assessor allotment details");
      }
    } catch (err) {
      setError(`Error loading assessor allotment details: ${err.message}`);
      console.error("Load assessor allotment error:", err);
      // Set empty data on error
      setAssessorsData([]);
    } finally {
      setLoading(false);
    }
  }, [appId]);

  // Load assessor file statistics
  const loadAssessorFileStats = useCallback(async () => {
    if (!orgId || !appId) return;

    try {
      const params = {
        orgId: orgId,
        appId: appId,
        Insertedby: localStorage.getItem('orgId')
      };

      const response = await DoApiService.genericGet('/nc-main/assessor-stats-inserted-by', params);

      if (response.success && response.data) {
        // Convert array to object with assessorId as key for easy lookup
        const statsMap = {};
        response.data.forEach(stat => {
          statsMap[stat.Assessorid] = {
            fileCount: stat.FileCount || 0,
            clauseNumbers: stat.ClauseNumbers || '',
            lastUploadDate: stat.LastUploadDate,
            allDocuments: stat.AllDocuments || ''
          };
        });
        setAssessorFileStats(statsMap);
      } else {
        setAssessorFileStats({});
      }
    } catch (error) {
      console.error('Error loading assessor file statistics:', error);
      setAssessorFileStats({});
    }
  }, [orgId, appId]);

  // Load equipment data for QR code generation
  const loadEquipmentData = useCallback(async () => {
    if (!orgId || !appId) return;

    setLoadingEquipments(true);
    try {

      // Try to use the laboratory service to get equipment data
      const response = await LaboratoryService.getexistingallEquipments(appId, orgId);

      if (response && response.success && response.data) {
        setEquipmentData(response.data);
      } else {
        // If laboratory service doesn't work, try alternative API
        const alternativeResponse = await DoApiService.genericGet('/equipment/list', {
          orgId: orgId,
          appId: appId
        });

        if (alternativeResponse && alternativeResponse.success && alternativeResponse.data) {
          setEquipmentData(alternativeResponse.data);
        } else {
          setEquipmentData([]);
        }
      }
    } catch (error) {
      console.error('Error loading equipment data:', error);
      setEquipmentData([]);
    } finally {
      setLoadingEquipments(false);
    }
  }, [orgId, appId]);


  const watchedCoordinates = watch(['latitude', 'longitude']);

  //const loadClauseNo = useCallback(async (assessorId, userType = '1') => {
  //    if (!orgId || !appId) return;

  //   try {
  //     const response = await DoApiService.getClauseNoList({
  //       standard: '2022', 
  //       feild: 'Medical', 
  //       appid: appId, 
  //       orgid: assessorId, 
  //       userType: userType
  //     });
  //     if (response.isstatus === true) {
  //       setClauseNoList(response.clause);
  //     } else {
  //       throw new Error(response.message || "Failed to load clause number");
  //     }
  //   } catch (error) {
  //     console.error("Error loading clause number:", error);
  //     setClauseNoList([]);
  //   }
  // }, [orgId, appId]);

  const loadClauseNo = useCallback(async (assessorId, userType = '1', labTypeId) => {
    if (!orgId || !appId) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/scrutiny-remarks/da-clause-nc`,
        {
          params: {
            standard: (labTypeId === 1|| labTypeId === 2) ? '2017' : '2022',
            feild:  labTypeId === 1 ? 'Testing' : labTypeId === 2 ? 'Calibration' : 'Medical',
          }
        }
      );

      if (response.data.success === true) {
        setClauseNoList(response.data.data || []);
      } else {
        throw new Error(response.data.message || "Failed to load clause number");
      }
    } catch (error) {
      console.error("Error loading clause number:", error);
      setClauseNoList([]);
    }
  }, [orgId, appId]);

  // Fetch facilities for location update
  const fetchFacilities = async () => {
    if (!appId) return;

    try {
      setFacilitiesLoading(true);
      const response = await LaboratoryService.getFacilityDetails(appId);
      if (response.success) {
        // Filter out PermanentFacility for labTypeId 1 and 2
        const filteredFacilities = (labTypeId === 1 || labTypeId === 2) 
          ? (response.data || []).filter(facility => facility.FacilityName !== 'PermanentFacility')
          : response.data || [];
        setFacilities(filteredFacilities);
        
        // Initialize location data structure for each facility (single location per facility)
        const initialLocations = {};
        filteredFacilities.forEach(facility => {
          initialLocations[facility.FacilityName] = {
            id: null,
            locationName: '', 
            address: '', 
            latitude: '', 
            longitude: '', 
            type: facility.FacilityName,
            isLabSend: null,
            isApprove: null,
            disApproveRemarks: null,
          };
        });
        setFacilityLocations(initialLocations);
      }
    } catch (error) {
      console.error("Error fetching facility details:", error);
      setFacilities([]);
    } finally {
      setFacilitiesLoading(false);
    }
  };

  useEffect(() => {
    // Check if user is authenticated and parameters exist
    if (!orgId || !appId) {
      toast.error('Invalid parameters. Please navigate from dashboard.');
      navigate('/main/dashboard');
      return;
    }

    // Set formData with appId
    setFormData({ appId });

    // Fetch application stage
    getApplicationStage(appId);

    // Load existing location data
    loadAssessorAllotmentData();
    loadLocationData();
    loadAssessorFileStats();
    loadEquipmentData();
  }, [orgId, appId, navigate, loadAssessorAllotmentData, loadAssessorFileStats, loadEquipmentData]);

  // Fetch facilities when labTypeId is available
  useEffect(() => {
    if (appId && labTypeId) {
      fetchFacilities();
    }
  }, [appId, labTypeId]);

  // Load existing facility locations after facilities are loaded
  useEffect(() => {
    if (appId && orgId && facilities.length > 0) {
      loadExistingFacilityLocations();
    }
  }, [appId, orgId, facilities]);

  // Load existing location data
  const loadLocationData = async () => {
    try {
      setPageLoading(true);
      setError('');

      // Get existing location data
      const locationResult = await LocationAPI.getLocation(orgId);

      if (locationResult.success && locationResult.data) {
        const locationData = locationResult.data;
        setCurrentLocation(locationData);

        // Pre-fill form with existing data
        setValue('latitude', locationData.latitude || locationData.Latitude || '');
        setValue('longitude', locationData.longitude || locationData.Longitude || '');

        // Set validation status if coordinates exist
        const lat = locationData.latitude || locationData.Latitude;
        const lng = locationData.longitude || locationData.Longitude;
        if (lat && lng) {
          setCoordinatesValid(true);
        }

        // Set lab info from location data
        setLabInfo({
          orgId: locationData.OrgId,
          labName: locationData.LabName || 'Laboratory',
          address: locationData.Address || 'N/A',
          lastUpdated: locationData.UpdatedDate,
        });
      }
    } catch (error) {
      console.error('Error loading location data:', error);
      setError('Failed to load location data');
    } finally {
      setPageLoading(false);
    }
  };

  // Debounced validation effect
  useEffect(() => {
    // Auto-validate coordinates when they change (with debouncing)
    const [lat, lng] = watchedCoordinates;
    if (lat && lng && !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng))) {
      const latNum = parseFloat(lat);
      const lngNum = parseFloat(lng);

      // Check if these coordinates have already been validated
      if (lastValidatedRef.current.lat === latNum && lastValidatedRef.current.lng === lngNum) {
        return; // Skip validation if coordinates haven't changed
      }

      // Debounce the validation to avoid rapid API calls
      const timer = setTimeout(() => {
        validateCoordinates(latNum, lngNum);
      }, 500); // Wait 500ms after user stops typing

      return () => clearTimeout(timer); // Cleanup timer on re-render
    } else {
      // Clear validation if coordinates are invalid
      setCoordinatesValid(null);
    }
  }, [watchedCoordinates]);

  // Validate coordinates with backend API
  const validateCoordinates = async (latitude, longitude) => {
    try {
      setValidating(true);
      setError('');

      // Store coordinates being validated to prevent duplicate calls
      lastValidatedRef.current = { lat: latitude, lng: longitude };

      const result = await LocationAPI.validateCoordinates(latitude, longitude);

      if (result.success) {
        setCoordinatesValid(result.valid);
        if (!result.valid) {
          setError(result.message || 'Invalid coordinates. Please ensure coordinates are within India.');
        }
      } else {
        setCoordinatesValid(false);
        setError(result.message || 'Failed to validate coordinates');
      }
    } catch (error) {
      console.error('Error validating coordinates:', error);
      setCoordinatesValid(false);
      setError('Failed to validate coordinates. Please try again.');
    } finally {
      setValidating(false);
    }
  };

  // Get current location from browser
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        const errorMsg = 'Geolocation is not supported by this browser';
        toast.error(errorMsg);
        reject(new Error(errorMsg));
        return;
      }

      setGettingLocation(true);
      setError('');

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setValue('latitude', latitude.toFixed(6));
          setValue('longitude', longitude.toFixed(6));
          setGettingLocation(false);
          toast.success(`Current location detected: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
          resolve({ latitude, longitude });
        },
        (error) => {
          setGettingLocation(false);
          let errorMessage = 'Failed to get current location';

          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied. Please enable location permissions.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out.';
              break;
            default:
              break;
          }

          toast.error(errorMessage);
          setError(errorMessage);
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0 // Don't use cached position - get fresh location
        }
      );
    });
  };

  // Submit location coordinates
  const onSubmit = async (data) => {
    setLoading(true);
    setError('');

    try {
      // Get current location first
      await getCurrentLocation();

      // Get the updated coordinates from form
      const currentFormData = watch();
      const latitude = currentFormData.latitude;
      const longitude = currentFormData.longitude;

      if (coordinatesValid === false) {
        toast.error('Please enter valid coordinates within India');
        return;
      }

      // Now update the location in database
      const result = await LocationAPI.submitLocation(
        orgId,
        latitude,
        longitude
      );

      if (result.success) {
        toast.success('Location data updated successfully');

        // Reload the location data to reflect changes
        await loadLocationData();
      } else {
        setError(result.message || 'Failed to update location data');
        toast.error(result.message || 'Failed to update location data');
      }
    } catch (error) {
      console.error('Error updating location:', error);
      const errorMessage = error.message || 'Failed to update location data. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Load existing files for the selected assessor
  const loadExistingFiles = async (assessorId) => {
    if (!orgId || !appId || !assessorId) return;

    try {
      setLoadingExistingFiles(true);

      const params = {
        orgId: orgId,
        appId: appId,
        assessorId: assessorId,
        Insertedby: localStorage.getItem('orgId')
      };

      const response = await DoApiService.genericGet('/nc-main/records-insertedby', params);

      if (response.success && response.data) {
        // Process the documents to show individual files
        const filesData = response.data.map(record => {
          const documents = record.Evidence ? record.Evidence.split('~') : [];
          return documents.map(doc => ({
            id: record.id, // Add record id for deletion
            fileName: doc.split('/').pop() || doc, // Get filename from path
            fullPath: doc,
            clauseNo: record.Clauseno,
            uploadDate: record.InsertDate,
            fullUrl: doc.startsWith('http') ? doc : `${S3_BASE_URL}${doc}` // Reconstruct full URL if needed
          }));
        }).flat();

        setExistingFiles(filesData);
      } else {
        setExistingFiles([]);
      }
    } catch (error) {
      console.error('Error loading existing files:', error);
      setExistingFiles([]);
    } finally {
      setLoadingExistingFiles(false);
    }
  };

  // Handle delete file confirmation
  const handleDeleteClick = (file) => {
    setFileToDelete(file);
    setDeleteConfirmOpen(true);
  };

  // Handle delete file execution
  const handleDeleteFile = async () => {
    if (!fileToDelete || !fileToDelete.id) {
      toast.error('Invalid file data for deletion');
      return;
    }

    try {
      setDeletingFile(true);

      const response = await axios.put(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Deleteevidence`,
        null,
        {
          params: { id: fileToDelete.id }
        }
      );

      if (response.data.isstatus === true) {
        toast.success('File deleted successfully');

        // Refresh existing files list
        if (currentAssessor && currentAssessor.allotedToId) {
          await loadExistingFiles(currentAssessor.allotedToId);
        }

        // Refresh assessor file stats
        await loadAssessorFileStats();
      } else {
        toast.error(response.data.message || 'Failed to delete file');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete file. Please try again.');
    } finally {
      setDeletingFile(false);
      setDeleteConfirmOpen(false);
      setFileToDelete(null);
    }
  };

  // Handle delete confirmation cancel
  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setFileToDelete(null);
  };

  // Check lab details for assessment stop status
  const checkLabDetails = async (assessorId) => {
    if (!appId || !assessorId) {
      console.error('Missing appId or assessorId for lab details check');
      return false;
    }

    setLoadingLabDetails(true);

    try {
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Getlabdetails?appid=${appId}&orgid=${assessorId}`;

      const response = await fetch(url);
      const data = await response.json();

      setLabDetails(data);

      // Check if assessment is stopped
      const isAssessmentStopped = data?.stopAssessment === "true" || data?.stopAssessment === true;
      setAssessmentStopped(isAssessmentStopped);

      if (isAssessmentStopped) {
        toast.warning('Assessment has been stopped for this laboratory. File upload for clause numbers is disabled.');
        console.warn('⚠️ Assessment stopped for lab:', assessorId);
      }

      return isAssessmentStopped;
    } catch (error) {
      console.error('❌ Error checking lab details:', error);
      toast.error('Failed to check lab details. Please try again.');
      setAssessmentStopped(false);
      return false;
    } finally {
      setLoadingLabDetails(false);
    }
  };

  // Handle Upload Evidence button click
  const handleUploadEvidence = async (assessor, index) => {
    const assessorId = assessor.allotedToId;

    // Map assessor's capacity to userType based on the specified rules
    let userType = '4'; // Default for other cases
    const capacity = assessor.capacity?.toLowerCase() || '';

    if (capacity.includes('lead assessor') && capacity.includes('technical assessor')) {
      userType = '3'; // Lead and Technical Assessor
    } else if (capacity.includes('lead assessor')) {
      userType = '1'; // Lead Assessor
    } else if (capacity.includes('technical assessor')) {
      userType = '2'; // Technical Assessor
    }


    // Load clause numbers with the mapped userType
    loadClauseNo(assessorId, userType,labTypeId);

    // First check lab details to see if assessment is stopped
    const isAssessmentStopped = await checkLabDetails(assessorId);

    setCurrentAssessor({ ...assessor, index, assessmentStopped: isAssessmentStopped });
    setSelectedFiles([]);
    setSelectedClauseNo(null); // Reset clause number selection
    setExistingFiles([]); // Reset existing files

    // Load existing files for this assessor
    if (assessorId) {
      loadExistingFiles(assessorId);
    }

    setUploadPopupOpen(true);
  };

  // Handle Test Witness button click
  const handleTestWitness = (assessor) => {
    try {
      const assessorId = assessor.allotedToId;

      // Try to determine lab type from various sources
      let labType = '3'; // Default assumption

      // You can enhance this logic based on your data structure
      if (assessor.labType) {
        labType = assessor.labType;
      } else if (assessor.disciplineType) {
        labType = assessor.disciplineType;
      } else if (window.location.pathname.includes('medical')) {
        labType = '3';
      } else if (window.location.pathname.includes('testing')) {
        labType = '1';
      } else if (window.location.pathname.includes('calibration')) {
        labType = '2';
      }

      // Check if we have all required data
      if (!appId || !orgId || !assessorId) {
        toast.error('Missing required data for navigation. Please ensure assessor ID is available.');
        console.error('Missing data:', { appId, orgId, assessorId, assessor });
        return;
      }

      // Get assessment ID with fallback options
      const assessmentId = assessor.assessmentId || assessor.AssessmentId || assessor.id || '';

      // Construct the navigation URL
      let testWitnessUrl;
      if(labTypeId === 2) {
        testWitnessUrl = `/main/laboratory/scope-witness-calibration?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&labType=${encryptText(labTypeId)}&assessorId=${encryptText(assessorId)}&assessmentId=${encryptText(assessmentId)}&type=${encryptText('lab')}`;
      } else {
        testWitnessUrl = `/main/laboratory/scope-witness?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&labType=${encryptText(labType)}&assessorId=${encryptText(assessorId)}&assessmentId=${encryptText(assessmentId)}&type=${encryptText('lab')}`;
      }

      // Navigate to the test witness page
      navigate(testWitnessUrl);
    } catch (error) {
      console.error('Error navigating to test witness page:', error);
      toast.error('Error navigating to test witness page: ' + error.message);
    }
  };
  const handlefeedbackNavigate = (assessor) => {
    const assessmentId = assessor.assessmentId || '';
    const feedbackUrl = `/main/laboratory/feedback?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&assessmentId=${encryptText(assessmentId)}`;
    window.open(feedbackUrl, '_blank');
    //navigate(feedbackUrl);
  };

  const handleNCReply = (assessor) => {
    try {
      const assessorId = assessor.allotedToId;

      // Try to determine lab type from various sources
      let labType = '3'; // Default assumption

      // You can enhance this logic based on your data structure
      if (assessor.labType) {
        labType = assessor.labType;
      } else if (assessor.disciplineType) {
        labType = assessor.disciplineType;
      } else if (window.location.pathname.includes('medical')) {
        labType = '3';
      } else if (window.location.pathname.includes('testing')) {
        labType = '1';
      } else if (window.location.pathname.includes('calibration')) {
        labType = '2';
      }

      // Check if we have all required data
      if (!appId || !orgId || !assessorId) {
        toast.error('Missing required data for navigation. Please ensure assessor ID is available.');
        console.error('Missing data:', { appId, orgId, assessorId, assessor });
        return;
      }

      // Get assessment ID with fallback options
      const assessmentId = assessor.assessmentId || '';

      // Construct the navigation URL
      const ncReplyUrl = `/main/laboratory/nc-reply?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&labType=${encryptText(labType)}&assessorId=${encryptText(assessorId)}&assessmentId=${encryptText(assessmentId)}&type=${encryptText('lab')}`;


      // Navigate to the NC Reply page
      navigate(ncReplyUrl);
    } catch (error) {
      console.error('Error navigating to NC Reply page:', error);
      toast.error('Error navigating to NC Reply page: ' + error.message);
    }
  };

  // Handle file selection with automatic S3 upload
  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Add files to selected list first
    setSelectedFiles(prevFiles => [...prevFiles, ...files]);

    // Start auto-uploading to S3
    setAutoUploading(true);

    // Extract assessor ID with comprehensive fallback logic (consistent with submit function)
    const assessorId = currentAssessor.allotedToId;

    try {

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const timestamp = Date.now();
        const customFileName = `${assessorId}_${timestamp}_${s3UploadedFiles.length + i}_${file.name}`;
        const folder = `evidence`; // Will be used as section in S3 service

        try {
          // Add progress callback for better UX
          const progressCallback = (percent) => {
            console.log(`Upload progress for ${file.name}: ${percent}%`);
          };

          const uploadResult = await s3Service.uploadFile(file, folder, customFileName, progressCallback);

          if (uploadResult.success) {
            const s3FileData = {
              originalName: file.name,
              s3FileName: uploadResult.data.fileName || uploadResult.data.imgName,
              s3Url: uploadResult.data.url || uploadResult.data.directUrl || uploadResult.data.publicUrl,
              s3Key: uploadResult.data.key,
              fileSize: file.size,
              uploadTime: new Date().toISOString()
            };

            // Add to S3 uploaded files list
            setS3UploadedFiles(prevS3Files => [...prevS3Files, s3FileData]);
          } else {
            throw new Error(`Failed to upload ${file.name}: ${uploadResult.error}`);
          }
        } catch (uploadError) {
          console.error('Auto S3 Upload failed:', uploadError);
          toast.error(`❌ Auto-upload failed for ${file.name}: ${uploadError.message}`);
        }
      }

      toast.success(`🎉 ${files.length} files automatically uploaded to S3!`);
    } catch (error) {
      console.error('Auto-upload error:', error);
      toast.error(`❌ Auto-upload error: ${error.message}`);
    } finally {
      setAutoUploading(false);
      // Reset the input value to allow selecting the same file again
      event.target.value = '';
    }
  };

  // Remove selected file and corresponding S3 file
  const handleRemoveFile = (indexToRemove) => {
    setSelectedFiles(prevFiles =>
      prevFiles.filter((_, index) => index !== indexToRemove)
    );

    // Also remove from S3 uploaded files list
    setS3UploadedFiles(prevS3Files =>
      prevS3Files.filter((_, index) => index !== indexToRemove)
    );

    toast.info('File removed from upload list');
  };

  // Handle saving S3 URLs to database (files already uploaded)
  const handleSubmitFiles = async () => {
    // Check if assessment is stopped
    if (currentAssessor?.assessmentStopped) {
      toast.error('Cannot submit files. Assessment has been stopped for this laboratory.');
      return;
    }

    if (s3UploadedFiles.length === 0) {
      toast.error('No files have been uploaded to S3 yet. Please select files first.');
      return;
    }

    if (!selectedClauseNo) {
      toast.error('Please select a Clause Number before submitting files.');
      return;
    }

    if (!currentAssessor) {
      toast.error('No assessor selected. Please close and reopen the upload dialog.');
      return;
    }

    // Extract assessor ID with comprehensive fallback logic
    const assessorId = currentAssessor.allotedToId
    // Default fallback

    // Get assessor name for logging
    const assessorName = currentAssessor.assessorName ||
      currentAssessor.name ||
      currentAssessor.AssessorName ||
      'Unknown Assessor';


    try {
      setUploadLoading(true);

      // Function to remove S3 base URL from full URL
      const removeS3BaseUrl = (fullUrl) => {
        if (!fullUrl || typeof fullUrl !== 'string') {
          console.warn('Invalid URL provided to removeS3BaseUrl:', fullUrl);
          return fullUrl;
        }

        if (fullUrl.startsWith(S3_BASE_URL)) {
          const relativePath = fullUrl.replace(S3_BASE_URL, '');
          return relativePath;
        }

        return fullUrl; // Return original if base URL not found
      };

      const s3Urls = s3UploadedFiles.map(f => removeS3BaseUrl(f.s3Url)).join('~');

      // Prepare NC_main record data (only 6 required columns)
      const ncData = {
        OrgId: parseInt(orgId),
        AppId: parseInt(appId),
        Assessorid: parseInt(assessorId), // Ensure it's sent as integer
        ClauseNo: selectedClauseNo.displayName,
        Document: s3Urls, // Store S3 URLs joined with ~
        InsertDate: new Date().toISOString(),
        Insertedby: localStorage.getItem('orgId'),
        labTypeId: labTypeId || 0,
        standard: labTypeId === 3 ? '2022' : '2017'
      };


      // Call API to insert NC_main record
      const response = await DoApiService.genericPost('/nc-main/insert', ncData);

      if (response.success) {
        toast.success(`🎉 SUCCESS! ${s3UploadedFiles.length} file paths saved for ${assessorName} (ID: ${assessorId}) - Clause ${selectedClauseNo.displayName}`);



        // Update the assessor data to mark as completed
        const updatedAssessorsData = [...assessorsData];
        updatedAssessorsData[currentAssessor.index] = {
          ...updatedAssessorsData[currentAssessor.index],
          UploadEvidence: 'Completed',
          S3Files: s3UploadedFiles.length,
          S3Section: 'evidence',
          S3Folder: 'live/',
          S3Urls: s3Urls
        };
        setAssessorsData(updatedAssessorsData);

        // Reload file statistics to update counts
        loadAssessorFileStats();

        // Close popup and reset
        setUploadPopupOpen(false);
        setSelectedFiles([]);
        setS3UploadedFiles([]);
        setSelectedClauseNo(null);
        setExistingFiles([]);
        setCurrentAssessor(null);
      } else {
        // If NC_main insertion fails but S3 files already uploaded
        console.error('S3 files uploaded but database save failed:', response);
        throw new Error(`Files are in S3 but database save failed: ${response.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving to database:', error);

      if (error.message.includes('Files are in S3')) {
        toast.error(`⚠️ Files are in S3 but database save failed: ${error.message}`);
      } else if (s3UploadedFiles.length > 0) {
        toast.error(`❌ Database save failed: ${s3UploadedFiles.length} files are in S3 but not saved to database: ${error.message}`);
      } else {
        toast.error(`❌ Save failed: ${error.message}`);
      }
    } finally {
      setUploadLoading(false);
    }
  };

  // Close upload popup
  const handleCloseUploadPopup = () => {
    setUploadPopupOpen(false);
    setSelectedFiles([]);
    setS3UploadedFiles([]);
    setSelectedClauseNo(null);
    setExistingFiles([]);
    setCurrentAssessor(null);
  };

  // Handle back navigation
  const handleBackToDashboard = () => {
    navigate('/main/dashboard');
  };

  // Download Equipment QR Codes as PDF
  const downloadEquipmentQRCodes = async () => {
    if (!equipmentData || equipmentData.length === 0) {
      toast.warning('No equipment data available to generate QR codes');
      return;
    }

    setQrCodeDownloading(true);
    try {

      // Create PDF document
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // PDF styling
      const margin = 15;
      const qrSize = 25; // QR code size in mm
      const cellWidth = (pageWidth - 2 * margin) / 5; // 5 columns
      const cellHeight = qrSize + 25; // QR + text space
      const cols = 5;
      let currentRow = 0;
      let currentCol = 0;
      let pageNumber = 1;

      // Calculate how many rows fit per page
      const headerSpace = 40; // Space for title and date
      const availableHeight = pageHeight - (2 * margin) - headerSpace;
      const rowsPerPage = Math.floor(availableHeight / cellHeight);

      // Add header
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Laboratory Equipment QR Codes', pageWidth / 2, margin, { align: 'center' });

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Generated on: ${new Date().toLocaleString()}`, pageWidth / 2, margin + 7, { align: 'center' });

      // Starting position for QR codes
      let startY = margin + 20;
      let processedCount = 0;
      let skippedCount = 0;

      for (let i = 0; i < equipmentData.length; i++) {
        const equipment = equipmentData[i];
        // Use multiple possible field names for equipment ID
        const equipmentId = equipment.EquipmentId || equipment.equipmentId || equipment.Id || equipment.id || equipment.equipmentid;
        const equipmentdisId = equipment.Equipmentid || 'N/A';
        const equipmentUid = equipment.uid || equipment.UID || equipment.Uid || equipmentId;
        const equipmentName = equipment.NameOfEquipment || equipment.equipmentName || equipment.name || equipment.Name || 'Unknown Equipment';
        const disciplineName = equipment.DisciplineName || equipment.disciplineName || 'Unknown Discipline';

        if (!equipmentId) {
          console.warn(`No equipment ID found for equipment at index ${i}:`, equipment);
          skippedCount++;
          continue;
        }

        // Generate QR code URL using the same pattern as Medical Equipment Section
        const qrUrl = `${equipmentId}~${orgId}`;

        // Check if we need a new page
        let currentY = startY + (currentRow * cellHeight);
        if (currentY + cellHeight > pageHeight - margin) {
          pdf.addPage();
          pageNumber++;

          // Add header to new page
          pdf.setFontSize(16);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Laboratory Equipment QR Codes', pageWidth / 2, margin, { align: 'center' });

          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          pdf.text(`Generated on: ${new Date().toLocaleString()}`, pageWidth / 2, margin + 7, { align: 'center' });

          startY = margin + 20;
          currentRow = 0;
          currentCol = 0;
          currentY = startY + (currentRow * cellHeight);
        }

        // Calculate position
        const x = margin + (currentCol * cellWidth);
        const y = currentY;

        try {
          // Generate QR code as base64 image
          const qrCodeDataURL = await QRCodeLib.toDataURL(qrUrl, {
            width: 100,
            margin: 2,
            color: {
              dark: '#000000',
              light: '#FFFFFF'
            }
          });

          // Add QR code to PDF
          pdf.addImage(qrCodeDataURL, 'PNG', x + (cellWidth - qrSize) / 2, y, qrSize, qrSize);

          // Add equipment ID below QR code
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'bold');
          pdf.text(`UID: ${equipmentUid}`, x + cellWidth / 2, y + qrSize + 5, { align: 'center', maxWidth: cellWidth - 4 });

          // Add equipment ID below QR code
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'bold');
          pdf.text(`EquipmentID: ${equipmentdisId}`, x + cellWidth / 2, y + qrSize + 10, { align: 'center', maxWidth: cellWidth - 4 });

          // Add discipline name with proper spacing
          pdf.setFontSize(6);
          pdf.setFont('helvetica', 'normal');
          pdf.text(`Discipline: ${disciplineName}`, x + cellWidth / 2, y + qrSize + 15, { align: 'center', maxWidth: cellWidth - 4 });

          // Add equipment name (truncated if too long)
          pdf.setFontSize(7);
          pdf.setFont('helvetica', 'normal');
          const truncatedName = equipmentName.length > 25 ? equipmentName.substring(0, 22) + '...' : equipmentName;
          pdf.text(truncatedName, x + cellWidth / 2, y + qrSize + 22, { align: 'center', maxWidth: cellWidth - 4 });

          // Add border around cell
          pdf.setDrawColor(200, 200, 200);
          pdf.rect(x, y, cellWidth, cellHeight);

          processedCount++;

        } catch (error) {
          console.error(`Error generating QR code for equipment ${equipmentId}:`, error);

          // Add error placeholder
          pdf.setFontSize(8);
          pdf.text('QR Error', x + cellWidth / 2, y + qrSize / 2, { align: 'center' });
          pdf.text(`ID: ${equipmentId}`, x + cellWidth / 2, y + qrSize + 7, { align: 'center' });

          processedCount++;
        }

        // Move to next position
        currentCol++;
        if (currentCol >= cols) {
          currentCol = 0;
          currentRow++;
        }
      }

      console.log('📊 Laboratory QR Code Processing Summary:');
      console.log('Processed successfully:', processedCount);
      console.log('Skipped due to missing data:', skippedCount);
      console.log('Total equipments:', equipmentData.length);

      // Add page numbers
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
      }

      // Save PDF
      const fileName = `Laboratory_Equipment_QR_Codes_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

      const successMessage = processedCount === equipmentData.length
        ? `QR codes PDF downloaded successfully! (${processedCount} equipments)`
        : `QR codes PDF downloaded! (${processedCount} processed, ${skippedCount} skipped out of ${equipmentData.length} total equipments)`;

      toast.success(successMessage);

    } catch (error) {
      console.error('Error generating QR codes PDF:', error);
      toast.error('Error generating QR codes PDF: ' + error.message);
    } finally {
      setQrCodeDownloading(false);
    }
  };

  // Handler functions for facility locations
  const handleLocationChange = (facilityName, field, value) => {
    setFacilityLocations(prev => ({
      ...prev,
      [facilityName]: {
        ...prev[facilityName],
        [field]: value
      }
    }));
  };

  // Get current location for a specific facility
  const handleGetFacilityLocation = (facilityName) => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        const errorMsg = 'Geolocation is not supported by this browser';
        toast.error(errorMsg);
        reject(new Error(errorMsg));
        return;
      }

      setGettingFacilityLocation(facilityName);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setFacilityLocations(prev => ({
            ...prev,
            [facilityName]: {
              ...prev[facilityName],
              latitude: latitude.toFixed(6),
              longitude: longitude.toFixed(6)
            }
          }));
          setGettingFacilityLocation(null);
          toast.success(`Location detected for ${getFacilityTypeDisplayName(facilityName)}: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
          resolve({ latitude, longitude });
        },
        (error) => {
          setGettingFacilityLocation(null);
          let errorMessage = 'Failed to get current location';

          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied. Please enable location permissions.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out.';
              break;
            default:
              break;
          }

          toast.error(errorMessage);
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });
  };

  const getAddressFromLatLng = async (lat, lng) => {
  try {
    if (!lat || !lng) {
    console.log("Latitude or Longitude missing", lat, lng);
    return "";
  }
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`
    );
    const data = await response.json();
    console.log('Reverse geocoding result:', JSON.stringify(data));
    console.log('Reverse geocoding result:', data.display_name);
    return data.display_name || "";
  } catch (error) {
    console.error(error);
    return "";
  }
};

  const handleSaveSingleFacilityLocation = async (facilityName) => {
    try {
      setLoading(true);
      
      const location = facilityLocations[facilityName];
      
      // Validate that location has required fields
      if (!location.locationName || !location.address || !location.latitude || !location.longitude) {
        toast.error(`Please fill all fields for ${getFacilityTypeDisplayName(facilityName)}`);
        setLoading(false);
        return;
      }
      
      // Validate latitude range
      const lat = parseFloat(location.latitude);
      if (location.latitude && (isNaN(lat) || lat < -90 || lat > 90)) {
        toast.error(`Invalid latitude for ${getFacilityTypeDisplayName(facilityName)}. Must be between -90 and 90`);
        setLoading(false);
        return;
      }
      
      // Validate longitude range
      const lng = parseFloat(location.longitude);
      if (location.longitude && (isNaN(lng) || lng < -180 || lng > 180)) {
        toast.error(`Invalid longitude for ${getFacilityTypeDisplayName(facilityName)}. Must be between -180 and 180`);
        setLoading(false);
        return;
      }

      const locationData = {
        appId,
        orgId,
        locationFacility: location
      };

      // Call API to save location
      const response = await CalibrationLaboratoryService.saveFacilityLocations(locationData);
      
      if (response.success) {
        toast.success(`${getFacilityTypeDisplayName(facilityName)} location saved successfully!`);
        // Optionally reload the locations
        await loadExistingFacilityLocations();
      } else {
        toast.error(response.message || 'Failed to save facility location');
      }
      
    } catch (error) {
      console.error('Error saving facility location:', error);
      toast.error(error.message || 'Failed to save facility location');
    } finally {
      setLoading(false);
    }
  };

  // Load existing facility locations from database
  const loadExistingFacilityLocations = async () => {
    if (!appId || !orgId) return;

    try {
      const response = await CalibrationLaboratoryService.getFacilityLocations(appId, orgId);
      if (response.success && response.data && response.data.length > 0) {
        // Convert array to object structure
        const existingLocations = {};
        response.data.forEach(loc => {
          existingLocations[loc.FacilityType] = {
            id: loc.Id || null,
            locationName: loc.LocationName || '',
            address: loc.Address || '',
            latitude: loc.Latitude || '',
            longitude: loc.Longitude || '',
            type: loc.FacilityType,
            isLabSend: loc.isLabSend,
            isApprove: loc.isApprove,
            disApproveRemark: loc.disApproveRemark || ''
          };
        });

        // Merge with existing facility locations
        setFacilityLocations(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(facilityType => {
            if (existingLocations[facilityType]) {
              updated[facilityType] = existingLocations[facilityType];
            }
          });
          return updated;
        });
      }
    } catch (error) {
      console.error('Error loading existing facility locations:', error);
      // Don't show error toast, as this is optional data
    }
  };

  // Handle Send for Approval for a single facility location
  const handleSendFacilityForApproval = async (facilityName) => {
    const location = facilityLocations[facilityName];
    
    if (!location || !location.id) {
      toast.error(`Please save the ${getFacilityTypeDisplayName(facilityName)} location first before sending for acceptance`);
      return;
    }

    // Validate that all required fields are filled
    if (!location.locationName || !location.address || !location.latitude || !location.longitude) {
      toast.error(`Please fill all fields for ${getFacilityTypeDisplayName(facilityName)} before sending for acceptance`);
      return;
    }

    // Open password dialog
    setPendingApprovalFacility(facilityName);
    setPassword('');
    setPasswordDialogOpen(true);
  };

  // Handle password verification and send for acceptance
  const handlePasswordVerifyAndSend = async () => {
    if (!password.trim()) {
      toast.error('Please enter your password');
      return;
    }

    try {
      setPasswordVerifying(true);

      // Verify password
      const passwordResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/verify-password`,
        {
          orgId: localStorage.getItem('orgId'),
          password: password,
        }
      );

      if (!passwordResponse.data.success) {
        toast.error('Invalid password');
        return;
      }

      // Password verified, proceed with sending for approval
      const location = facilityLocations[pendingApprovalFacility];
      
      const response = await CalibrationLaboratoryService.sendFacilityLocationForApproval(
        appId,
        orgId,
        location.id,
        1 // isLabSend = 1 (sent for acceptance)
      );
      
      if (response.success) {
        toast.success(`${getFacilityTypeDisplayName(pendingApprovalFacility)} sent for acceptance successfully!`);
        
        // Send email notification to DO
        try {
          const emailResponse = await axios.post(
            `${process.env.REACT_APP_API_URL}/assessment-mail/send-approval-notification`,
            {
              appId: appId,
              orgId: orgId,
              recipientType: 'DO',
              facilityName: `${getFacilityTypeDisplayName(pendingApprovalFacility)} - ${location.locationName || 'N/A'}`
            }
          );
          console.log('Email sent to DO:', emailResponse.data);
        } catch (emailError) {
          console.error('Failed to send email to DO:', emailError.message);
          // Don't fail the main operation if email fails
        }
        
        // Reload the locations to get updated status
        await loadExistingFacilityLocations();
        // Close dialog
        setPasswordDialogOpen(false);
        setPendingApprovalFacility(null);
        setPassword('');
      } else {
        toast.error(response.message || 'Failed to send facility location for acceptance');
      }
      
    } catch (error) {
      console.error('Error sending facility location for acceptance:', error);
      toast.error(error.message || 'Failed to send facility location for acceptance');
    } finally {
      setPasswordVerifying(false);
    }
  };

  // Close password dialog
  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setPendingApprovalFacility(null);
    setPassword('');
  };

  // Helper function to get facility type display name
  const getFacilityTypeDisplayName = (facilityName) => {
    const typeMap = {
      'SiteFacility': 'Site Facility',
      'MobileFacility': 'Mobile Facility',
      'permanentSiteFacility': 'Permanent Site Facility'
    };
    return typeMap[facilityName] || facilityName;
  };

  if (pageLoading) {
    return (
      <LoaderWrapper
        loading={true}
        type="fullscreen"
        message="Loading Laboratory Update Details..."
      />
    );
  }

  return (
    <Box
      sx={{
        mt: 8,
        minHeight: "100vh",
        py: 4,
        bgcolor: "#f5f5f5",
      }}
    >
      <Container maxWidth="xl">
        {/* Breadcrumbs */}
        <Box sx={{ mb: 3 }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link
              underline="hover"
              color="inherit"
              onClick={handleBackToDashboard}
              sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
              Dashboard
            </Link>
            <Typography
              color="text.primary"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <UpdateIcon sx={{ mr: 0.5, fontSize: 20 }} />
              Laboratory Update Details
            </Typography>
          </Breadcrumbs>
        </Box>

        <Grid container spacing={4}>
          {/* Lab Location Update Table */}
          <Grid item xs={12}>
            <Card elevation={2} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <LocationIcon
                    sx={{ color: "#667eea", mr: 2, fontSize: 32 }}
                  />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "#333" }}
                  >
                    Laboratory Location Update
                  </Typography>
                </Box>

                {/* Mobile App Info */}
                <Alert
                  severity="info"
                  icon={
                    <Box
                      component="span"
                      sx={{
                        fontSize: "24px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      📱
                    </Box>
                  }
                  sx={{
                    mb: 3,
                    background:
                      "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
                    border: "1px solid #90caf9",
                    borderRadius: 2,
                    "& .MuiAlert-message": {
                      width: "100%",
                      py: 0.5,
                    },
                    "& .MuiAlert-icon": {
                      fontSize: 28,
                    },
                  }}
                >
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: "#1565c0",
                        mb: 0.5,
                      }}
                    >
                      🚀 Update Laboratory Location on the Go!
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#424242",
                        lineHeight: 1.6,
                      }}
                    >
                      You can also update your laboratory location conveniently
                      using our mobile application.
                    </Typography>
                    <Box
                      sx={{
                        mt: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#424242",
                          fontWeight: 500,
                        }}
                      >
                        Download the NABL App:
                      </Typography>
                      <Button
                        href="https://play.google.com/store/apps/details?id=com.a7techies.nabl_assessment_app"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="contained"
                        size="small"
                        sx={{
                          background:
                            "linear-gradient(135deg, #1976d2, #1565c0)",
                          color: "white",
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "0.813rem",
                          px: 2,
                          py: 0.75,
                          borderRadius: 1.5,
                          boxShadow: "0 2px 8px rgba(25, 118, 210, 0.3)",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #1565c0, #0d47a1)",
                            boxShadow: "0 4px 12px rgba(25, 118, 210, 0.4)",
                            transform: "translateY(-1px)",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        📲 Get it on Google Play
                      </Button>
                    </Box>
                  </Box>
                </Alert>

                {/* Current Location Info */}
                {currentLocation && (
                  <TableContainer
                    component={Paper}
                    sx={{ mb: 4, bgcolor: "#f8f9fa" }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#e3f2fd" }}>
                          <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Current Value
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Latitude</TableCell>
                          <TableCell>
                            <Chip
                              label={currentLocation.latitude || "Not Set"}
                              color={
                                currentLocation.latitude ? "success" : "default"
                              }
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Longitude</TableCell>
                          <TableCell>
                            <Chip
                              label={currentLocation.longitude || "Not Set"}
                              color={
                                currentLocation.longitude
                                  ? "success"
                                  : "default"
                              }
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Update Form */}
                {(!currentLocation || currentLocation.isDone !== true) && (
                  <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                    <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                      This is a One Time location update. The Assessment will be
                      based on the updated coordinates. please ensure accuracy.
                      Otherwise Assessment will not Start
                    </Alert>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Controller
                          name="latitude"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Latitude"
                              type="number"
                              inputProps={{ step: "0.000001" }}
                              error={!!errors.latitude}
                              helperText={errors.latitude?.message}
                              InputProps={{
                                readOnly: true,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <LocationIcon color="primary" />
                                  </InputAdornment>
                                ),
                                endAdornment: validating && (
                                  <InputAdornment position="end">
                                    <CircularProgress size={20} />
                                  </InputAdornment>
                                ),
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                  "&.Mui-focused": {
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor:
                                        coordinatesValid === true
                                          ? "#4caf50"
                                          : coordinatesValid === false
                                            ? "#f44336"
                                            : "#667eea",
                                    },
                                  },
                                },
                              }}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Controller
                          name="longitude"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Longitude"
                              type="number"
                              inputProps={{ step: "0.000001" }}
                              error={!!errors.longitude}
                              helperText={errors.longitude?.message}
                              InputProps={{
                                readOnly: true,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <LocationIcon color="primary" />
                                  </InputAdornment>
                                ),
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                  "&.Mui-focused": {
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor:
                                        coordinatesValid === true
                                          ? "#4caf50"
                                          : coordinatesValid === false
                                            ? "#f44336"
                                            : "#667eea",
                                    },
                                  },
                                },
                              }}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                          <Tooltip title="Get your current location automatically">
                            <Button
                              variant="outlined"
                              startIcon={
                                gettingLocation ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <MyLocationIcon />
                                )
                              }
                              onClick={getCurrentLocation}
                              disabled={gettingLocation}
                              sx={{
                                borderRadius: 2,
                                textTransform: "none",
                                borderColor: "#667eea",
                                color: "#667eea",
                                "&:hover": {
                                  borderColor: "#5a6fd8",
                                  bgcolor: "rgba(102, 126, 234, 0.04)",
                                },
                              }}
                            >
                              {gettingLocation
                                ? "Getting Location..."
                                : "Use Current Location"}
                            </Button>
                          </Tooltip>
                          {applicationStage === "20" && (
                            <Button
                              type="submit"
                              variant="contained"
                              startIcon={
                                loading ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <SaveIcon />
                                )
                              }
                              disabled={loading || coordinatesValid === false}
                              sx={{
                                borderRadius: 2,
                                textTransform: "none",
                                background:
                                  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                "&:hover": {
                                  background:
                                    "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                                },
                              }}
                            >
                              {loading ? "Updating..." : "Update Location"}
                            </Button>
                          )}
                        </Box>
                      </Grid>

                      {/* Validation Status */}
                      {coordinatesValid !== null && (
                        <Grid item xs={12}>
                          <Fade in timeout={300}>
                            <Alert
                              severity={coordinatesValid ? "success" : "error"}
                              icon={
                                coordinatesValid ? (
                                  <CheckCircleIcon />
                                ) : (
                                  <ErrorIcon />
                                )
                              }
                              sx={{ borderRadius: 2 }}
                            >
                              {coordinatesValid
                                ? "Coordinates are valid and within India"
                                : "Invalid coordinates. Please ensure coordinates are within India boundaries."}
                            </Alert>
                          </Fade>
                        </Grid>
                      )}

                      {/* Error Display */}
                      {error && (
                        <Grid item xs={12}>
                          <Alert severity="error" sx={{ borderRadius: 2 }}>
                            {error}
                          </Alert>
                        </Grid>
                      )}

                      {/* Progress Indicator */}
                      {(loading || validating) && (
                        <Grid item xs={12}>
                          <LinearProgress
                            sx={{
                              borderRadius: 1,
                              height: 6,
                              "& .MuiLinearProgress-bar": {
                                background:
                                  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              },
                            }}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}

                {/* Completion Message */}
                {currentLocation && currentLocation.isDone === true && (
                  <Alert
                    severity="success"
                    sx={{ mt: 2, borderRadius: 2 }}
                    icon={<CheckCircleIcon />}
                  >
                    Location update has been completed successfully. No further
                    changes are required at this stage.
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Facility Location Update Section - Only for labTypeId 1 and 2 */}
          {(labTypeId === 1 || labTypeId === 2) && facilities.length > 0 && (
            <Grid item xs={12}>
              <Card elevation={2} sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <BusinessIcon
                      sx={{ color: "#667eea", mr: 2, fontSize: 32 }}
                    />
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: "#333" }}
                    >
                      Facility Locations Update
                    </Typography>
                  </Box>

                  <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                    Update the location details for each facility type. Latitude
                    and longitude can be entered manually or fetched using the
                    "Get Current Location" button.
                  </Alert>

                  {facilitiesLoading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 3 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Box>
                      {facilities.map((facility, facilityIndex) => (
                        <Box
                          key={facilityIndex}
                          sx={{
                            mb: 4,
                            p: 3,
                            bgcolor: colors.grey[50],
                            borderRadius: 2,
                            border: `1px solid ${colors.grey[200]}`,
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: colors.primary.main,
                              mb: 2,
                            }}
                          >
                            {getFacilityTypeDisplayName(facility.FacilityName)}
                          </Typography>

                          {facilityLocations[facility.FacilityName] && (
                            <Box
                              sx={{
                                p: 2,
                                bgcolor: "white",
                                borderRadius: 2,
                                border: `1px solid ${colors.grey[300]}`,
                              }}
                            >
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    fullWidth
                                    label="Location Name"
                                    value={facilityLocations[facility.FacilityName].locationName
                                    }
                                    onChange={(e) =>
                                      handleLocationChange(
                                        facility.FacilityName,
                                        "locationName",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Enter location name"
                                    size="small"
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                      },
                                    }}
                                  />
                                </Grid>

                                <Grid item xs={12} md={8}>
                                  <TextField
                                    fullWidth
                                    label="Address"
                                    value={facilityLocations[facility.FacilityName].address}
                                    onChange={(e) =>
                                      handleLocationChange(
                                        facility.FacilityName,
                                        "address",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Enter complete address"
                                    size="small"
                                    multiline
                                    rows={1}
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                      },
                                    }}
                                  />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <TextField
                                    fullWidth
                                    label="Latitude"
                                    value={
                                      facilityLocations[facility.FacilityName]
                                        .latitude
                                    }
                                    placeholder="Latitude.."
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setFacilityLocations((prev) => ({
                                        ...prev,
                                        [facility.FacilityName]: {
                                          ...prev[facility.FacilityName],
                                          latitude: value,
                                        },
                                      }));
                                    }}
                                    size="small"
                                    inputProps={{
                                      step: "0.000001",
                                      min: -90,
                                      max: 90,
                                    }}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <LocationIcon
                                            sx={{
                                              fontSize: 18,
                                              color: colors.primary.main,
                                            }}
                                          />
                                        </InputAdornment>
                                      ),
                                    }}
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                      },
                                    }}
                                  />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <TextField
                                    fullWidth
                                    label="Longitude"
                                    value={
                                      facilityLocations[facility.FacilityName]
                                        .longitude
                                    }
                                    placeholder="Longitude.."
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setFacilityLocations((prev) => ({
                                        ...prev,
                                        [facility.FacilityName]: {
                                          ...prev[facility.FacilityName],
                                          longitude: value,
                                        },
                                      }));
                                    }}
                                    size="small"
                                    inputProps={{
                                      step: "0.000001",
                                      min: -180,
                                      max: 180,
                                    }}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <LocationIcon
                                            sx={{
                                              fontSize: 18,
                                              color: colors.secondary.main,
                                            }}
                                          />
                                        </InputAdornment>
                                      ),
                                    }}
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                      },
                                    }}
                                  />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <Button
                                    fullWidth
                                    variant="contained"
                                    startIcon={
                                      gettingFacilityLocation ===
                                      facility.FacilityName ? (
                                        <CircularProgress
                                          size={16}
                                          sx={{ color: "white" }}
                                        />
                                      ) : (
                                        <MyLocationIcon />
                                      )
                                    }
                                    onClick={() =>
                                      handleGetFacilityLocation(
                                        facility.FacilityName,
                                      )
                                    }
                                    disabled={
                                      gettingFacilityLocation ===
                                      facility.FacilityName
                                    }
                                    sx={{
                                      height: "40px",
                                      background: `linear-gradient(135deg, ${colors.success.main}, ${colors.success.dark})`,
                                      color: "white",
                                      borderRadius: 2,
                                      fontWeight: 500,
                                      textTransform: "none",
                                      boxShadow: `0 2px 8px ${alpha(colors.success.main, 0.3)}`,
                                      "&:hover": {
                                        boxShadow: `0 4px 12px ${alpha(colors.success.main, 0.4)}`,
                                        background: `linear-gradient(135deg, ${colors.success.dark}, ${colors.success.main})`,
                                      },
                                      "&:disabled": {
                                        background: colors.grey[300],
                                        color: colors.grey[500],
                                      },
                                    }}
                                  >
                                    {gettingFacilityLocation ===
                                    facility.FacilityName
                                      ? "Getting Location..."
                                      : "Get Current Location"}
                                  </Button>
                                </Grid>
                              </Grid>
                            </Box>
                          )}

                          {/* Save Button for this facility */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              gap: 2,
                              mt: 2,
                            }}
                          >
                            {(facilityLocations[facility.FacilityName]
                              .isLabSend === null ||
                              facilityLocations[facility.FacilityName]
                                .isLabSend === 0 ||
                              facilityLocations[facility.FacilityName]
                                .isLabSend === 2) &&
                              applicationStage === "20" && (
                                <Button
                                  variant="contained"
                                  startIcon={<SaveIcon />}
                                  onClick={() =>
                                    handleSaveSingleFacilityLocation(
                                      facility.FacilityName,
                                    )
                                  }
                                  disabled={loading}
                                  sx={{
                                    background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
                                    color: "white",
                                    px: 3,
                                    py: 1,
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    textTransform: "none",
                                    boxShadow: `0 4px 16px ${alpha(colors.primary.main, 0.3)}`,
                                    "&:hover": {
                                      boxShadow: `0 8px 24px ${alpha(colors.primary.main, 0.4)}`,
                                      transform: "translateY(-2px)",
                                      background: `linear-gradient(135deg, ${colors.primary.dark}, ${colors.secondary.dark})`,
                                    },
                                    "&:disabled": {
                                      background: colors.grey[300],
                                      color: colors.grey[500],
                                    },
                                    transition: "all 0.3s ease",
                                  }}
                                >
                                  {loading
                                    ? "Saving..."
                                    : `Save ${getFacilityTypeDisplayName(facility.FacilityName)}`}
                                </Button>
                              )}

                            {/* Send for Approval Button - Show when isLabSend is null, 0, or 2 and data is filled */}
                            {facilityLocations[facility.FacilityName] &&
                              (facilityLocations[facility.FacilityName]
                                .isLabSend === null ||
                                facilityLocations[facility.FacilityName]
                                  .isLabSend === 0 ||
                                facilityLocations[facility.FacilityName]
                                  .isLabSend === 2) &&
                              facilityLocations[facility.FacilityName]
                                .locationName &&
                              facilityLocations[facility.FacilityName]
                                .address &&
                              facilityLocations[facility.FacilityName]
                                .latitude &&
                              facilityLocations[facility.FacilityName]
                                .longitude &&
                              applicationStage === "20" && (
                                <Button
                                  variant="contained"
                                  startIcon={<SendIcon />}
                                  onClick={() =>
                                    handleSendFacilityForApproval(
                                      facility.FacilityName,
                                    )
                                  }
                                  disabled={loading}
                                  sx={{
                                    background: `linear-gradient(135deg, ${colors.success.main}, ${colors.success.dark})`,
                                    color: "white",
                                    px: 3,
                                    py: 1,
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    textTransform: "none",
                                    boxShadow: `0 4px 16px ${alpha(colors.success.main, 0.3)}`,
                                    "&:hover": {
                                      boxShadow: `0 8px 24px ${alpha(colors.success.main, 0.4)}`,
                                      transform: "translateY(-2px)",
                                      background: `linear-gradient(135deg, ${colors.success.dark}, ${colors.success.main})`,
                                    },
                                    "&:disabled": {
                                      background: colors.grey[300],
                                      color: colors.grey[500],
                                    },
                                    transition: "all 0.3s ease",
                                  }}
                                >
                                  Send for Dealing Officer Acceptance
                                </Button>
                              )}

                            {facilityLocations[facility.FacilityName]
                              ?.isLabSend === 1 &&
                              facilityLocations[facility.FacilityName]
                                ?.isApprove === 0 &&
                              applicationStage === "20" && (
                                <Typography
                                  variant="body2"
                                  color="warning.main"
                                  sx={{ mt: 1, fontWeight: 600 }}
                                >
                                  Pending NABL Acceptance
                                </Typography>
                              )}
                            {facilityLocations[facility.FacilityName]
                              ?.isLabSend === 1 &&
                              facilityLocations[facility.FacilityName]
                                ?.isApprove === 2 &&
                              applicationStage === "20" && (
                                <Typography
                                  variant="body2"
                                  sx={{ mt: 1, fontWeight: 500 }}
                                >
                                  <span
                                    style={{
                                      color: "#d32f2f",
                                      fontWeight: 600,
                                    }}
                                  >
                                    Rejected Remark:
                                  </span>{" "}
                                  {
                                    facilityLocations[facility.FacilityName]
                                      ?.disApproveRemark
                                  }
                                </Typography>
                              )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          <Grid item xs={12}>
            <Card elevation={2} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 3,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <BusinessIcon
                      sx={{ color: "#667eea", mr: 2, fontSize: 32 }}
                    />
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: "#333" }}
                    >
                      Assessor Allotment Details
                    </Typography>
                  </Box>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        const url = `/main/laboratory/form9?appId=${encryptAppId(
                          assessorsData[0].appId,
                        )}&assessmentId=${encryptText(
                          assessorsData[0].assessmentId,
                        )}&assessorId=${encryptText(assessorsData[0].allotedToId)}`;
                        window.open(url, "_blank", "noopener,noreferrer");
                      }}
                      sx={{
                        background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
                        color: "white",
                        px: 1,
                        py: 1,
                        borderRadius: 3,
                        fontWeight: 500,
                        fontSize: "0.75rem",
                        textTransform: "none",
                        boxShadow: `0 4px 16px ${alpha(
                          colors.primary.main,
                          0.3,
                        )}`,
                        "&:hover": {
                          boxShadow: `0 8px 24px ${alpha(
                            colors.primary.main,
                            0.4,
                          )}`,
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Form 9
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        if (assessorsData.length > 0 && assessorsData[0].NAF1) {
                          window.open(
                            assessorsData[0].NAF1,
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }
                      }}
                      sx={{
                        background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
                        color: "white",
                        px: 1,
                        py: 1,
                        borderRadius: 3,
                        fontWeight: 500,
                        fontSize: "0.75rem",
                        textTransform: "none",
                        boxShadow: `0 4px 16px ${alpha(
                          colors.primary.main,
                          0.3,
                        )}`,
                        "&:hover": {
                          boxShadow: `0 8px 24px ${alpha(
                            colors.primary.main,
                            0.4,
                          )}`,
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      NAF1
                    </Button>
                    {/* <Button
                      variant="outlined"
                      onClick={() => {
                        handlefeedbackNavigate(assessorsData[0]);
                      }}
                      sx={{
                        textTransform: "none",
                        borderColor: "#667eea",
                        color: "#667eea",
                        "&:hover": {
                          borderColor: "#5568d3",
                          color: "#5568d3",
                          backgroundColor: "rgba(102, 126, 234, 0.04)",
                        },
                      }}
                    >
                      Give Assessors Feedback
                    </Button> */}

                    {/* Download QR Codes Button */}
                    <Button
                      variant="outlined"
                      onClick={downloadEquipmentQRCodes}
                      disabled={
                        qrCodeDownloading ||
                        loadingEquipments ||
                        !equipmentData ||
                        equipmentData.length === 0
                      }
                      startIcon={
                        qrCodeDownloading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <QrCodeIcon />
                        )
                      }
                      sx={{
                        textTransform: "none",
                        borderColor: "#28a745",
                        color: "#28a745",
                        "&:hover": {
                          borderColor: "#218838",
                          color: "#218838",
                          backgroundColor: "rgba(40, 167, 69, 0.04)",
                        },
                        "&:disabled": {
                          borderColor: "#ccc",
                          color: "#ccc",
                        },
                      }}
                    >
                      {qrCodeDownloading
                        ? "Generating PDF..."
                        : `Download Equipment QR Codes ${
                            equipmentData ? `(${equipmentData.length})` : ""
                          }`}
                    </Button>
                  </div>
                </Box>

                {/* Assessors Table */}
                {assessorsData && assessorsData.length > 0 ? (
                  <TableContainer
                    component={Paper}
                    sx={{ mb: 4, bgcolor: "#f8f9fa" }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#e3f2fd" }}>
                          <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Report</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Upload Evidence
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>
                            Test Witness
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            NC Reply
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {assessorsData.map((assessor, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              "&:nth-of-type(odd)": { bgcolor: "#f9f9f9" },
                            }}
                          >
                            <TableCell>
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {assessor.assessorName ||
                                    assessor.name ||
                                    "-"}
                                  <br />

                                  {labTypeId === 1 || labTypeId === 2 ? (
                                    <>
                                      <div
                                        style={{
                                          color: "#1976d2",
                                          fontWeight: 500,
                                        }}
                                      >
                                        {assessor.disciplineName || "N/A"}
                                      </div>

                                      <div
                                        style={{
                                          color: "#2e7d32",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {assessor.groups || ""}
                                      </div>
                                    </>
                                  ) : assessor.disciplineName ? (
                                    `(${assessor.disciplineName})`
                                  ) : (
                                    "-"
                                  )}
                                </Typography>

                                <Chip
                                  label={assessor.assessorCapacity}
                                  size="small"
                                  color={index === 0 ? "primary" : "default"}
                                  sx={{ mb: 1, fontWeight: 600 }}
                                />
                              </Box>
                            </TableCell>
                            <TableCell>
                              {applicationStage === "21" &&
                                (assessor.assessorCapacity
                                  ?.toLowerCase()
                                  .includes("lead assessor") ||
                                  assessor.assessorCapacity
                                    ?.toLowerCase()
                                    .includes(
                                      "lead and technical assessor",
                                    )) && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 1,
                                    }}
                                  >
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() => {
                                        const url = `/main/laboratory/form217-preview?appId=${encryptAppId(
                                          appId,
                                        )}&assessmentId=${encryptText(
                                          assessor.assessmentId,
                                        )}&assessorId=${encryptText(
                                          assessor.allotedToId,
                                        )}`;
                                        window.open(
                                          url,
                                          "_blank",
                                          "noopener,noreferrer",
                                        );
                                      }}
                                      sx={{
                                        background: `linear-gradient(135deg, #667eea, #764ba2)`,
                                        color: "white",
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 2,
                                        fontWeight: 500,
                                        fontSize: "0.65rem",
                                        textTransform: "none",
                                        boxShadow: `0 2px 8px rgba(102, 126, 234, 0.3)`,
                                        "&:hover": {
                                          boxShadow: `0 4px 12px rgba(102, 126, 234, 0.4)`,
                                          transform: "translateY(-1px)",
                                        },
                                        transition: "all 0.3s ease",
                                      }}
                                    >
                                      View Report
                                    </Button>
                                    {labTypeId === 2 && (
                                      <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => {
                                          const url = `/main/laboratory/form73-calibration?appId=${encryptAppId(
                                            appId,
                                          )}&assessmentId=${encryptText(
                                            assessor.assessmentId,
                                          )}&assessorId=${encryptText(
                                            assessor.allotedToId,
                                          )}`;
                                          window.open(
                                            url,
                                            "_blank",
                                            "noopener,noreferrer",
                                          );
                                        }}
                                        sx={{
                                          background: `linear-gradient(135deg, #667eea, #764ba2)`,
                                          color: "white",
                                          px: 1,
                                          py: 0.5,
                                          borderRadius: 2,
                                          fontWeight: 500,
                                          fontSize: "0.65rem",
                                          textTransform: "none",
                                          boxShadow: `0 2px 8px rgba(102, 126, 234, 0.3)`,
                                          "&:hover": {
                                            boxShadow: `0 4px 12px rgba(102, 126, 234, 0.4)`,
                                            transform: "translateY(-1px)",
                                          },
                                          transition: "all 0.3s ease",
                                        }}
                                      >
                                        Form 73
                                      </Button>
                                    )}
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() => {
                                        const url = `/main/laboratory/nc-report?appId=${encryptAppId(
                                          appId,
                                        )}&assessmentId=${encryptText(
                                          assessor.assessmentId,
                                        )}&assessorId=${encryptText(
                                          assessor.allotedToId,
                                        )}`;
                                        window.open(
                                          url,
                                          "_blank",
                                          "noopener,noreferrer",
                                        );
                                      }}
                                      sx={{
                                        background: `linear-gradient(135deg, #667eea, #764ba2)`,
                                        color: "white",
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 2,
                                        fontWeight: 500,
                                        fontSize: "0.65rem",
                                        textTransform: "none",
                                        boxShadow: `0 2px 8px rgba(102, 126, 234, 0.3)`,
                                        "&:hover": {
                                          boxShadow: `0 4px 12px rgba(102, 126, 234, 0.4)`,
                                          transform: "translateY(-1px)",
                                        },
                                        transition: "all 0.3s ease",
                                      }}
                                    >
                                      View NC Report
                                    </Button>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() => {
                                        const url = `/main/laboratory/nc-application-finding?appId=${encryptAppId(
                                          appId,
                                        )}&assessmentId=${encryptText(
                                          assessor.assessmentId,
                                        )}&assessorId=${encryptText(
                                          assessor.allotedToId,
                                        )}&orgId=${encryptText(orgId)}`;
                                        window.open(
                                          url,
                                          "_blank",
                                          "noopener,noreferrer",
                                        );
                                      }}
                                      sx={{
                                        background: `linear-gradient(135deg, #667eea, #764ba2)`,
                                        color: "white",
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 2,
                                        fontWeight: 500,
                                        fontSize: "0.65rem",
                                        textTransform: "none",
                                        boxShadow: `0 2px 8px rgba(102, 126, 234, 0.3)`,
                                        "&:hover": {
                                          boxShadow: `0 4px 12px rgba(102, 126, 234, 0.4)`,
                                          transform: "translateY(-1px)",
                                        },
                                        transition: "all 0.3s ease",
                                      }}
                                    >
                                      Application Finding
                                    </Button>
                                  </Box>
                                )}
                              {applicationStage === "21" &&
                                assessor.stage === "5o" && (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => {
                                      const url = `/main/laboratoryNC/nc-reply?appId=${encryptAppId(
                                        appId,
                                      )}&assessmentId=${encryptText(
                                        assessor.assessmentId,
                                      )}&assessorId=${encryptText(
                                        assessor.allotedToId,
                                      )}&orgId=${encryptText(orgId)}`;
                                      window.open(
                                        url,
                                        "_blank",
                                        "noopener,noreferrer",
                                      );
                                    }}
                                    sx={{
                                      background: `linear-gradient(135deg, #10b981, #059669)`,
                                      color: "white",
                                      px: 1,
                                      py: 0.5,
                                      mt: 1,
                                      borderRadius: 2,
                                      fontWeight: 500,
                                      fontSize: "0.65rem",
                                      textTransform: "none",
                                      boxShadow: `0 2px 8px rgba(16, 185, 129, 0.3)`,
                                      "&:hover": {
                                        boxShadow: `0 4px 12px rgba(16, 185, 129, 0.4)`,
                                        transform: "translateY(-1px)",
                                      },
                                      transition: "all 0.3s ease",
                                    }}
                                  >
                                    Dealing Officer Clarification
                                  </Button>
                                )}
                            </TableCell>
                            <TableCell>
                              <>
                                {(() => {
                                  const assessorId = assessor.allotedToId;
                                  const fileStats =
                                    assessorFileStats[assessorId];
                                  const fileCount = fileStats?.fileCount || 0;

                                  return (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <Tooltip
                                        title={
                                          fileStats?.clauseNumbers
                                            ? `Files uploaded for clauses: ${fileStats.clauseNumbers}`
                                            : "No files uploaded yet"
                                        }
                                      >
                                        <Chip
                                          label={`${fileCount} files`}
                                          color={
                                            fileCount > 0
                                              ? "success"
                                              : "default"
                                          }
                                          size="small"
                                          variant={
                                            fileCount > 0
                                              ? "filled"
                                              : "outlined"
                                          }
                                          sx={{
                                            cursor:
                                              fileCount > 0
                                                ? "pointer"
                                                : "default",
                                            mb: 0.5,
                                          }}
                                          onClick={
                                            fileCount > 0
                                              ? () =>
                                                  handleUploadEvidence(
                                                    assessor,
                                                    index,
                                                  )
                                              : undefined
                                          }
                                        />
                                      </Tooltip>
                                      {fileStats?.lastUploadDate && (
                                        <Tooltip
                                          title={`Last upload: ${new Date(
                                            fileStats.lastUploadDate,
                                          ).toLocaleDateString()}`}
                                        >
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            �
                                          </Typography>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  );
                                })()}
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() =>
                                    handleUploadEvidence(assessor, index)
                                  }
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: "none",
                                    minWidth: "120px",
                                    borderColor:
                                      assessor.UploadEvidence === "Completed"
                                        ? "#4caf50"
                                        : "#667eea",
                                    color:
                                      assessor.UploadEvidence === "Completed"
                                        ? "#4caf50"
                                        : "#667eea",
                                    "&:hover": {
                                      borderColor:
                                        assessor.UploadEvidence === "Completed"
                                          ? "#388e3c"
                                          : "#5a6fd8",
                                      bgcolor:
                                        assessor.UploadEvidence === "Completed"
                                          ? "rgba(76, 175, 80, 0.04)"
                                          : "rgba(102, 126, 234, 0.04)",
                                    },
                                  }}
                                >
                                  {assessor.UploadEvidence === "Completed"
                                    ? "Completed"
                                    : "Upload Evidence"}
                                </Button>
                              </>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  flexDirection: "column",
                                }}
                              >
                                <Chip
                                  label={assessor.TestWitness || "Pending"}
                                  color={
                                    assessor.TestWitness === "Completed"
                                      ? "success"
                                      : "default"
                                  }
                                  size="small"
                                />
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<VisibilityIcon />}
                                  onClick={() => handleTestWitness(assessor)}
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: "none",
                                    minWidth: "120px",
                                    borderColor:
                                      assessor.TestWitness === "Completed"
                                        ? "#4caf50"
                                        : "#ff9800",
                                    color:
                                      assessor.TestWitness === "Completed"
                                        ? "#4caf50"
                                        : "#ff9800",
                                    "&:hover": {
                                      borderColor:
                                        assessor.TestWitness === "Completed"
                                          ? "#388e3c"
                                          : "#f57c00",
                                      bgcolor:
                                        assessor.TestWitness === "Completed"
                                          ? "rgba(76, 175, 80, 0.04)"
                                          : "rgba(255, 152, 0, 0.04)",
                                    },
                                  }}
                                >
                                  Test Witness
                                </Button>
                              </Box>
                            </TableCell>
                            {assessor.stage === "2" && (
                              <>
                                <TableCell>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => handleNCReply(assessor)}
                                    sx={{
                                      borderRadius: 2,
                                      textTransform: "none",
                                      minWidth: "120px",
                                      borderColor:
                                        assessor.NCReply === "Completed"
                                          ? "#4caf50"
                                          : "#002df5ff",
                                      color:
                                        assessor.NCReply === "Completed"
                                          ? "#4caf50"
                                          : "#002df5ff",
                                      "&:hover": {
                                        borderColor:
                                          assessor.NCReply === "Completed"
                                            ? "#388e3c"
                                            : "#002df5ff",
                                        bgcolor:
                                          assessor.NCReply === "Completed"
                                            ? "rgba(76, 175, 80, 0.04)"
                                            : "rgba(255, 152, 0, 0.04)",
                                      },
                                    }}
                                  >
                                    NC Reply
                                  </Button>
                                </TableCell>
                                {/* <TableCell>
                              <Chip 
                                label={assessor.AssessorReport || 'Pending'}
                                color={assessor.AssessorReport === 'Completed' ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell> */}
                              </>
                            )}
                            {assessor.NoNCobserve != 1 ? (
                              <>
                                {applicationStage === "21" &&
                                  (assessor.stage === "3" ||
                                    assessor.stage === "5g") &&
                                  assessor.NoNCobserve != 1 && (
                                    <>
                                      <TableCell>
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          startIcon={<VisibilityIcon />}
                                          onClick={() =>
                                            handleNCReply(assessor)
                                          }
                                          sx={{
                                            borderRadius: 2,
                                            textTransform: "none",
                                            minWidth: "120px",
                                            borderColor:
                                              assessor.NCReply === "Completed"
                                                ? "#4caf50"
                                                : "#002df5ff",
                                            color:
                                              assessor.NCReply === "Completed"
                                                ? "#4caf50"
                                                : "#002df5ff",
                                            "&:hover": {
                                              borderColor:
                                                assessor.NCReply === "Completed"
                                                  ? "#388e3c"
                                                  : "#002df5ff",
                                              bgcolor:
                                                assessor.NCReply === "Completed"
                                                  ? "rgba(76, 175, 80, 0.04)"
                                                  : "rgba(255, 152, 0, 0.04)",
                                            },
                                          }}
                                        >
                                          NC Reply
                                        </Button>
                                      </TableCell>
                                      {/* <TableCell>
                              <Chip 
                                label={assessor.AssessorReport || 'Pending'}
                                color={assessor.AssessorReport === 'Completed' ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell> */}
                                    </>
                                  )}
                              </>
                            ) : (
                              <Typography variant="caption">
                                No Nc Found For this Assessor
                              </Typography>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                    {loading
                      ? "Loading assessor data..."
                      : "No assessor allotment data available."}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* File Upload Dialog */}
        <Dialog
          open={uploadPopupOpen}
          onClose={handleCloseUploadPopup}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3 },
          }}
        >
          <DialogTitle sx={{ borderBottom: "1px solid #e0e0e0", pb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <CloudUploadIcon sx={{ color: "#667eea", fontSize: 32 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Upload Evidence Files
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Assessor:{" "}
                  {currentAssessor?.assessorName ||
                    currentAssessor?.name ||
                    "Unknown"}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ py: 3 }}>
            {/* Assessment Stopped Warning */}
            {currentAssessor?.assessmentStopped && (
              <Alert
                severity="warning"
                sx={{ mb: 3, borderRadius: 2 }}
                icon={<ErrorIcon />}
              >
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Assessment Stopped
                </Typography>
                <Typography variant="body2">
                  File upload for clause numbers has been disabled as the
                  assessment has been stopped for this laboratory.
                </Typography>
              </Alert>
            )}

            {/* Clause Number Selection - Hidden when assessment is stopped */}
            {!currentAssessor?.assessmentStopped && (
              <Box sx={{ mb: 3 }}>
                <Autocomplete
                  options={clauseNoList}
                  getOptionLabel={(option) => option.displayName || ""}
                  value={selectedClauseNo}
                  onChange={(event, newValue) => setSelectedClauseNo(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Clause Number *"
                      placeholder="Type to search or select from dropdown"
                      variant="outlined"
                      fullWidth
                      error={!selectedClauseNo}
                      helperText={
                        !selectedClauseNo
                          ? "Please select a clause number before uploading files"
                          : ""
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&.Mui-focused": {
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#667eea",
                            },
                          },
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.displayName}
                      </Typography>
                    </Box>
                  )}
                  sx={{ mb: 2 }}
                />
              </Box>
            )}

            {/* Existing Files Section */}
            {existingFiles.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 2, fontWeight: 600, color: "#334155" }}
                >
                  📁 Previously Uploaded Files ({existingFiles.length})
                </Typography>
                <Paper
                  sx={{
                    maxHeight: 200,
                    overflow: "auto",
                    bgcolor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <List dense>
                    {existingFiles.map((file, index) => (
                      <ListItem key={index} divider>
                        <AttachFileIcon sx={{ color: "#4caf50", mr: 2 }} />
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {file.fileName}
                              </Typography>
                              <Chip
                                label={`Clause ${file.clauseNo}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title="View file">
                            <IconButton
                              size="small"
                              onClick={() =>
                                window.open(file.fullUrl, "_blank")
                              }
                              sx={{ color: "#667eea", mr: 1 }}
                            >
                              <CloudUploadIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete file">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(file)}
                              sx={{ color: colors.error.main }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>
            )}

            {/* Loading State for Existing Files */}
            {loadingExistingFiles && (
              <Box sx={{ mb: 3, textAlign: "center" }}>
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Loading existing files...
                </Typography>
              </Box>
            )}

            {/* File Selection Area - Hidden when assessment is stopped */}
            {!currentAssessor?.assessmentStopped && (
              <Box
                sx={{
                  border: "2px dashed #cbd5e1",
                  borderRadius: 2,
                  p: 4,
                  textAlign: "center",
                  mb: 3,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "#667eea",
                    bgcolor: "rgba(102, 126, 234, 0.02)",
                  },
                  opacity: !selectedClauseNo ? 0.5 : 1,
                  pointerEvents: !selectedClauseNo ? "none" : "auto",
                }}
                onClick={() => document.getElementById("file-input").click()}
              >
                <input
                  id="file-input"
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                  accept=".pdf,.xls,.xlsx"
                  disabled={!selectedClauseNo}
                />
                <CloudUploadIcon
                  sx={{ fontSize: 48, color: "#94a3b8", mb: 2 }}
                />
                <Typography variant="h6" sx={{ mb: 1, color: "#334155" }}>
                  {!selectedClauseNo
                    ? "Select Clause Number First"
                    : "Select Files to Upload"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {!selectedClauseNo
                    ? "Please select a clause number before uploading files"
                    : "Click here or drag and drop files. Multiple files are supported."}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  Supported formats: PDF, Excel (XLS, XLSX)
                </Typography>
              </Box>
            )}

            {/* Selected Files List with S3 Status */}
            {selectedFiles.length > 0 && (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Selected Files ({selectedFiles.length})
                  {s3UploadedFiles.length > 0 && (
                    <Chip
                      label={`${s3UploadedFiles.length}/${selectedFiles.length} uploaded to S3`}
                      color="success"
                      size="small"
                      sx={{ ml: 2 }}
                    />
                  )}
                </Typography>
                <List
                  sx={{
                    bgcolor: "#f8f9fa",
                    borderRadius: 2,
                    maxHeight: 200,
                    overflow: "auto",
                  }}
                >
                  {selectedFiles.map((file, index) => {
                    const s3File = s3UploadedFiles[index];
                    const isUploaded = s3File !== undefined;

                    return (
                      <ListItem key={index} divider>
                        <AttachFileIcon
                          sx={{
                            color: isUploaded ? "#4caf50" : "#667eea",
                            mr: 2,
                          }}
                        />
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {file.name}
                              {isUploaded && (
                                <Chip
                                  label="S3 ✓"
                                  color="success"
                                  size="small"
                                />
                              )}
                              {autoUploading &&
                                index === selectedFiles.length - 1 && (
                                  <CircularProgress size={16} />
                                )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="caption">
                                Size: {(file.size / 1024 / 1024).toFixed(2)} MB
                              </Typography>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => handleRemoveFile(index)}
                            sx={{ color: "#d32f2f" }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            )}

            {/* Database Save Progress */}
            {uploadLoading && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <CircularProgress size={20} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      💾 Saving S3 URLs to database...
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      S3 URLs are being saved to NC_main table with ~ separator
                    </Typography>
                  </Box>
                </Box>
              </Alert>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3, borderTop: "1px solid #e0e0e0" }}>
            <Button
              onClick={handleCloseUploadPopup}
              variant="outlined"
              disabled={uploadLoading}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                borderColor: "#94a3b8",
                color: "#64748b",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitFiles}
              variant="contained"
              disabled={
                currentAssessor?.assessmentStopped ||
                s3UploadedFiles.length === 0 ||
                uploadLoading ||
                autoUploading ||
                !selectedClauseNo
              }
              startIcon={
                uploadLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              sx={{
                borderRadius: 2,
                textTransform: "none",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                },
                "&:disabled": {
                  background: "#e2e8f0",
                  color: "#94a3b8",
                },
              }}
            >
              {currentAssessor?.assessmentStopped
                ? "Assessment Stopped - Cannot Save Files"
                : uploadLoading
                  ? "Saving to Database..."
                  : autoUploading
                    ? "Uploading to S3..."
                    : !selectedClauseNo
                      ? "Select Clause Number First"
                      : s3UploadedFiles.length === 0
                        ? "Select Files First"
                        : `Save ${s3UploadedFiles.length} files for Clause ${selectedClauseNo.displayName}`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={handleDeleteCancel}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: colors.error.light, color: "white" }}>
            <Box display="flex" alignItems="center" gap={1}>
              <DeleteIcon />
              Confirm Delete
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to delete this file?
            </Typography>
            {fileToDelete && (
              <Box sx={{ bgcolor: "#f8f9fa", p: 2, borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  File: {fileToDelete.fileName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Clause: {fileToDelete.clauseNo}
                </Typography>
              </Box>
            )}
            <Alert severity="warning" sx={{ mt: 2 }}>
              This action cannot be undone.
            </Alert>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleDeleteCancel}
              variant="outlined"
              disabled={deletingFile}
              sx={{
                borderRadius: 2,
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteFile}
              variant="contained"
              color="error"
              disabled={deletingFile}
              startIcon={
                deletingFile ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DeleteIcon />
                )
              }
              sx={{
                borderRadius: 2,
                textTransform: "none",
              }}
            >
              {deletingFile ? "Deleting..." : "Delete File"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Password Verification Dialog for Send for Approval */}
        <Dialog
          open={passwordDialogOpen}
          onClose={handleClosePasswordDialog}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, color: colors.primary.main }}>
            Password Verification
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Please enter your password to send facility location for
                acceptance.
              </Alert>
              <TextField
                fullWidth
                type="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && !passwordVerifying) {
                    handlePasswordVerifyAndSend();
                  }
                }}
                placeholder="Enter your password"
                autoFocus
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button
              onClick={handleClosePasswordDialog}
              disabled={passwordVerifying}
              sx={{
                borderRadius: 2,
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handlePasswordVerifyAndSend}
              disabled={passwordVerifying || !password.trim()}
              startIcon={
                passwordVerifying ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
              sx={{
                borderRadius: 2,
                textTransform: "none",
                background: `linear-gradient(135deg, ${colors.success.main}, ${colors.success.dark})`,
              }}
            >
              {passwordVerifying ? "Verifying..." : "Verify & Send"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Back to Dashboard Button */}
        <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToDashboard}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              px: 4,
              py: 1.5,
              borderColor: "#667eea",
              color: "#667eea",
              "&:hover": {
                borderColor: "#5a6fd8",
                bgcolor: "rgba(102, 126, 234, 0.04)",
              },
            }}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default LaboratoryUpdateDetailsPage;